/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import { getUserLanguageCode } from 'js/lib/language';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import FloatToPercent from 'bundles/primitive-formatting/components/FloatToPercent';
import { NARROW_XDP_CONTAINER_QUERY } from 'bundles/unified-description-page-common/constants/styles';

import _t from 'i18n!nls/reviews-common';

type RatingCountsByStar = {
  fiveStarRatingCount?: number | null;
  fourStarRatingCount?: number | null;
  threeStarRatingCount?: number | null;
  twoStarRatingCount?: number | null;
  oneStarRatingCount?: number | null;
};

type Props = {
  averageFiveStarRating?: number | null;
  ratingCountsByStar?: RatingCountsByStar | null;
  // Omit (or pass null/undefined) to render the average rating and bar graph without a review count
  // -- e.g. when that data point isn't available.
  ratingCount?: number | null;
  numberOfCourses?: number;
};

const styles = {
  barGraphContainer: css`
    max-width: 315px;
    padding: 0 0 var(--cds-spacing-300) 0;
    vertical-align: middle;
    display: table;
    border-collapse: collapse;
    white-space: nowrap;

    li {
      border-bottom: var(--cds-spacing-100) solid transparent;
    }

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin-bottom: var(--cds-spacing-200);
    }

    ${NARROW_XDP_CONTAINER_QUERY} {
      max-width: 100%;
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  graphRow: css`
    width: 100%;
    display: table-row;
  `,
  starBarLabel: css`
    display: table-cell;
    position: relative;
    vertical-align: middle;
  `,
  barGraphBar: css`
    height: 6px;
    width: 100%;
    display: table-cell;
    vertical-align: middle;
    padding-top: 2px;
    position: relative;
    white-space: nowrap;
  `,
  numRatings: css`
    white-space: nowrap;
    display: table-cell;
    vertical-align: middle;
  `,
  // Ref: https://codepen.io/jedtrow/pen/YPrqKY
  greyBar: css`
    content: '';
    top: 0;
    left: 0;
    width: 100%;
    position: relative;
    height: 6px;
    background: var(--cds-color-grey-50);
    border-radius: 8px;
  `,
  blueBar: (maxWidthAsPercentage: string) => css`
    content: '';
    top: 0;
    left: 0;
    position: relative;
    height: 6px;
    background: var(--cds-color-blue-800);
    display: block;
    width: ${maxWidthAsPercentage};
    border-radius: 8px;
  `,
  ratingsSummary: css`
    display: inline-flex;
    align-items: baseline;
    margin-bottom: var(--cds-spacing-300);
  `,
  ratingsSummarySingleLine: css`
    display: inline-flex;
    align-items: center;
  `,
  star: css`
    margin-right: var(--cds-spacing-50);
  `,
  rowWhiteSpace: css`
    display: inline-block;
    width: 0.5em;
  `,
  dotSeparator: css`
    margin-left: var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary-weak);
  `,
  courseAverageSubtext: css`
    margin-top: calc(var(--cds-spacing-50) - var(--cds-spacing-300));
    margin-bottom: var(--cds-spacing-200);
  `,
};

const STAR_RATING_COUNT_KEYS = [
  ['fiveStarRatingCount', 5],
  ['fourStarRatingCount', 4],
  ['threeStarRatingCount', 3],
  ['twoStarRatingCount', 2],
  ['oneStarRatingCount', 1],
] as const;

const RatingsBreakdown = ({ averageFiveStarRating, ratingCountsByStar, ratingCount, numberOfCourses }: Props) => {
  if (!ratingCountsByStar) {
    return null;
  }

  const userLocale = getUserLanguageCode();

  const totalStarRatingCounts = Object.values(ratingCountsByStar).reduce((acc: number, curr) => {
    if (curr && typeof curr === 'number') {
      return acc + curr;
    }
    return acc;
  }, 0);

  const fixedRating = averageFiveStarRating?.toFixed(1);

  return (
    <React.Fragment>
      <div css={styles.ratingsSummary}>
        <div
          role="img"
          aria-roledescription="rating"
          aria-label={
            ratingCount
              ? _t('#{fixedRating} stars, #{ratingCount} reviews', {
                  fixedRating,
                  ratingCount: ratingCount.toLocaleString(userLocale),
                })
              : _t('#{fixedRating} stars', { fixedRating })
          }
        >
          <StarFilledIcon color="default" css={styles.star} aria-hidden="true" />
          {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
          <Typography variant="h1semibold" component="span" aria-hidden="true">
            {fixedRating}
          </Typography>
          {ratingCount ? (
            <Typography2 component="p" variant="bodySecondary" aria-hidden="true">
              <FormattedMessage
                message={_t('{ratingCount} reviews')}
                ratingCount={<FormattedNumber value={ratingCount} />}
              />
            </Typography2>
          ) : null}
        </div>
      </div>
      {typeof numberOfCourses === 'number' && (
        <Typography2 component="p" variant="bodySecondary" css={styles.courseAverageSubtext}>
          {_t('avg. across #{numberOfCourses} courses', { numberOfCourses })}
        </Typography2>
      )}
      {totalStarRatingCounts > 0 && (
        <div data-unit="reviews-bar-graph" data-testid="reviews-bar-graph">
          <ul css={styles.barGraphContainer}>
            {STAR_RATING_COUNT_KEYS.map(([starKey, numStarIndex]) => {
              const starCountNum = Number(ratingCountsByStar[starKey]);
              const percentageAsDecimal = starCountNum / totalStarRatingCounts;
              // A CSS value, not display text -- must use a plain `.` decimal separator
              // regardless of locale. `numberRenders.Formatters.percentage` is locale-aware
              // (e.g. renders "1,2%" in some locales), which is both invalid CSS and a source
              // of SSR/client hydration mismatches on this element's className.
              const starPercentage = `${(percentageAsDecimal * 100).toFixed(2)}%`;

              if (!numStarIndex) {
                return null;
              }

              return (
                <li key={`${numStarIndex}-star`} css={styles.graphRow}>
                  <span css={styles.starBarLabel}>
                    <Typography2 variant="subtitleMedium" component="span">
                      <FormattedMessage
                        message={_t('{numStarIndex, plural, =1 {# star} other {# stars}}')}
                        numStarIndex={numStarIndex}
                      />
                    </Typography2>
                  </span>
                  <span css={styles.rowWhiteSpace} />
                  <span css={styles.barGraphBar}>
                    <div css={styles.greyBar}>
                      <div css={styles.blueBar(starPercentage)} />
                    </div>
                  </span>
                  <span css={styles.rowWhiteSpace} />
                  <span css={styles.numRatings}>
                    <Typography2 component="p" variant="bodySecondary">
                      <FloatToPercent value={percentageAsDecimal} />
                    </Typography2>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </React.Fragment>
  );
};

export type { RatingCountsByStar };
export default RatingsBreakdown;
